import { ThunkDispatch } from '@reduxjs/toolkit';
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from '@reach/router';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Formik, Form, Field, FieldProps } from 'formik';
import { IProviders, RootState } from 'store/store';
import PublicProperties from 'store/entity/PublicProperties';
import {
  updateProvider,
  updateProviderFn,
} from 'store/providers/providerSlice';
import { socialNetworkIdPattern } from 'store/entity/patterns';
import messages from './Messages';
import Input from 'components/Input';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import { IProvider } from 'store/providers/providerTypes';
import cloneDeep from 'lodash/cloneDeep';

interface IStateToProps {
  thisProvider: IProviders['map'][0];
}

interface IDispatchToProps {
  updateProvider: ({
    provider,
  }: {
    provider: IProvider;
  }) => ReturnType<typeof updateProviderFn>;
}

interface IOwnProps {
  providerId?: number;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  saving: boolean;
}

interface IFormValues {
  email: string;
  aparat: string;
  website: string;
  facebook: string;
  linkedIn: string;
  telegramId: string;
  instagramId: string;
  telegramChanel: string;
}

class SocialNetworks extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      saving: false,
    };
  }

  save = (values: IFormValues) => {
    this.setState({ saving: true });
    const { thisProvider } = this.props;
    const provider = cloneDeep(thisProvider.data);

    // @ts-expect-error it's ok
    provider.publicProperties = new PublicProperties(
      null,
      values.telegramChanel,
      values.telegramId,
      values.instagramId,
      values.facebook,
      null,
      values.linkedIn,
      values.aparat,
      values.website,
      values.email,
      null,
      provider?.publicProperties?.workingContext
    );
    return this.props.updateProvider({ provider: provider! }).then(
      () => {
        this.setState({ saving: false });
        notify(messages.successfullySaved);
      },
      (err: any) => {
        this.setState({ saving: false });
        display(err);
      }
    );
  };

  render() {
    const { thisProvider } = this.props;

    const initialValues = {
      website: thisProvider.data?.publicProperties?.website || '',
      email: thisProvider.data?.publicProperties?.email || '',
      telegramChanel: thisProvider.data?.publicProperties?.telegramChanel || '',
      telegramId: thisProvider.data?.publicProperties?.telegramId || '',
      instagramId: thisProvider.data?.publicProperties?.instagramId || '',
      aparat: thisProvider.data?.publicProperties?.aparat || '',
      facebook: thisProvider.data?.publicProperties?.facebook || '',
      linkedIn: thisProvider.data?.publicProperties?.linkedIn || '',
    };
    return (
      <Fragment>
        <Helmet>
          <title>{messages.socialNetworks}</title>
          <meta name="description" content={messages.socialNetworks} />
        </Helmet>
        <Formik
          onSubmit={this.save}
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {() => (
            // @ts-expect-error JSX error
            <Form style={{ overflow: 'auto' }}>
              <div className="row">
                <div className="col m6 s12">
                  <Field
                    name="website"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="website"
                        icon="globe"
                        label={messages.website}
                        placeholder={messages.website}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="email"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="email"
                        icon="mail"
                        label={messages.email}
                        placeholder={messages.email}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="telegramChanel"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="telegramChanel"
                        icon="send"
                        label={messages.telegram_chanel}
                        pattern={socialNetworkIdPattern}
                        placeholder={messages.telegram_chanel}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="telegramId"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="telegramId"
                        icon="send"
                        label={messages.telegram_user}
                        pattern={socialNetworkIdPattern}
                        placeholder={messages.telegram_user}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="instagramId"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="instagramId"
                        icon="instagram"
                        label={messages.instagram}
                        pattern={socialNetworkIdPattern}
                        placeholder={messages.instagram}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="aparat"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="aparat"
                        icon="youtube"
                        label={messages.aparat}
                        placeholder={messages.aparat}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="facebook"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="facebook"
                        icon="facebook"
                        label={messages.facebook}
                        placeholder={messages.facebook}
                      />
                    )}
                  />
                </div>
                <div className="col m6 s12">
                  <Field
                    name="linkedIn"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="text"
                        id="linkedIn"
                        icon="linkedin"
                        label={messages.linkedIn}
                        placeholder={messages.linkedIn}
                      />
                    )}
                  />
                </div>
                <div className="col m12 s12">
                  <Button
                    type="submit"
                    color="green"
                    style={{ width: '100%', margin: '2rem 0' }}
                    loading={this.state.saving}
                  >
                    {messages.save}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const providerId = Number(props.providerId);

  const thisProvider = state.providers.map[providerId];

  return {
    thisProvider,
  };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  updateProvider: ({ provider }: { provider: IProvider }) =>
    dispatch(updateProvider({ provider })).unwrap(),
});

export default connect(mapStateToProps, mapDispatchToProps)(SocialNetworks);
